import React, { useRef, useEffect, useState, useMemo } from 'react';

interface MhrMetadata {
  typeCode: string; // e.g., '60591-5' or '28616-1'
  title?: string;
  [key: string]: any;
}

interface MhrDocumentViewerProps {
  content: string; // The raw HTML string or Base64 PDF string
  metadata: MhrMetadata;
  onLinkClicked(target: string): void | undefined;
}

const MhrDocumentViewer: React.FC<MhrDocumentViewerProps> = ({ content, metadata, onLinkClicked }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState<boolean>(false);

  useEffect(() => {
    // 1. Detection Logic
    const detectContent = () => {
      if (!content) return;

      // Check metadata if available (LOINC codes for non-XML documents)
      const nonXmlCodes = ['28616-1', '18748-4'];
      const isKnownNonXml = metadata && nonXmlCodes.includes(metadata.typeCode);

      // Check for PDF signatures in the content string (Base64 for %PDF-)
      // JVBERi0 is the Base64 start for '%PDF-'
      const isPdfBinary = content.trim().startsWith('JVBERi0');

      if (isKnownNonXml || isPdfBinary) {
        setIsPdf(true);
        // Create a blob URL for the iframe to display
        const binaryString = window.atob(content.trim());
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        setPdfUrl(URL.createObjectURL(blob));
      } else {
        setIsPdf(false);
      }
    };

    detectContent();

    // 2. Anchor Link Interception (only for HTML view)
    const handleAnchorClick = (event: any) => {
      const anchor = event.target.closest('a');
      if (anchor && anchor.getAttribute('href')?.startsWith('#')) {
        event.preventDefault();
        const targetId = anchor.getAttribute('href').slice(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    const container = containerRef.current;
    if (container && !isPdf) {
      container.addEventListener('click', handleAnchorClick);
    }

    return () => {
      if (container) container.removeEventListener('click', handleAnchorClick);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl); // Cleanup memory
    };
  }, [content, metadata, isPdf]);


  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: Validate the message type and origin if possible
      if (event.data?.type === 'MHR_LINK_CLICKED' && onLinkClicked) {
        onLinkClicked(event.data.url);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onLinkClicked]);

  // Define a list of known static help documents provided by the ADHA
  // These will NOT be treated as API-fetchable attachments.
  const ADHA_STATIC_HELP_FILES = [
    'MedicalConditionsViewHelp.pdf',
    'MedsViewHelp.pdf',
    'PathologyViewHelp.pdf',
    'DiagnosticImagingViewHelp.pdf',
    'SharedHealthSummaryHelp.pdf',
    'DischargeSummaryHelp.pdf',
    'EventSummaryHelp.pdf',
    'logo.png'
  ];

  // 2. Define the navigation script to be injected
  const anchorScript = `

    <script>
    (function() {
      const staticHelpFiles = ${JSON.stringify(ADHA_STATIC_HELP_FILES)};
      
      document.addEventListener('click', function(e) {
        const anchor = e.target.closest('a');
        // Intercept internal hash links to prevent parent URL reset
        e.preventDefault(); 
        const targetRef = anchor?.getAttribute('href');
        if (targetRef && targetRef.startsWith('#')) {
          const targetId = anchor.getAttribute('href').slice(1);
          const targetEl = document.getElementById(targetId) || document.getElementsByName(targetId)[0];
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth' });
          }
          return;
        }
        
        // 2. Verified Static Help Assets
        if (staticHelpFiles[targetRef]) {
          const helpFile = staticHelpFiles[targetRef];
          window.open(helpFile, '_blank');
          return;
        }
      
        // 3. General Help Catch-all (for any other *Help.pdf files)
        if (targetRef.toLowerCase().endsWith('help.pdf')) {
          window.open('https://www.digitalhealth.gov.au/healthcare-providers/initiatives-and-programs/my-health-record', '_blank');
        }
        
        if (targetRef) {
          // If target element is not found, send the anchor click event to the parent React component
          console.log('ref clicked', targetRef);
          e.preventDefault();
          window.parent.postMessage({
              type: 'MHR_LINK_CLICKED',
              url: targetRef
            }, '*'); // Use specific origin in production for security          
          }
       });
    })();
    </script>
  `;

  // 3. Anchor Link Navigation Logic
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: Validate the message type and origin if possible
      if (event.data?.type === 'MHR_LINK_CLICKED' && onLinkClicked) {
        onLinkClicked(event.data.url);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onLinkClicked]);

  const srcDoc = useMemo(() => {
    if (isPdf || !content) return '';

    // 3. Inject the script before the closing </body> tag
    if (content.toLowerCase().includes('</body>')) {
      return content.replace(/<\/body>/i, `${anchorScript}</body>`);
    }

    // Fallback: If no body tag is found, simply append it
    return `${content}${anchorScript}`;

  }, [content, isPdf]);

  // --- Render PDF View (e.g. Specialist Letter) ---
  if (isPdf && pdfUrl) {
    return (
      <iframe
        src={pdfUrl}
        title="MHR PDF Document"
        width="100%"
        height="100%"
        style={{ border: 'none' }}
      />
    );
  }

  // --- Render Sanitized HTML View (e.g. Shared Health Summary) ---
  return (
    <iframe
      srcDoc={srcDoc}
      title={metadata.title || ''}
      width="100%"
      height="100%"
      style={{ border: 'none' }}
      sandbox="allow-scripts allow-same-origin allow-modals" // Restricts the iframe for security
    />
  );
};

export default MhrDocumentViewer;